import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, Clock, MessageSquare, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface MeetingRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MeetingRequestDialog({ isOpen, onClose }: MeetingRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    date: "",
    timeSlot: "Afternoon",
    topic: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: "Meeting Request",
          service: "Discovery Call",
          message: `REQUESTED DATE: ${formData.date}\nTIME SLOT: ${formData.timeSlot}\n\nTOPIC: ${formData.topic || "General Inquiry"}`,
        }),
      });

      if (!res.ok) throw new Error("Failed to submit request");

      setIsSuccess(true);
      toast.success("Meeting request sent! We'll confirm via email.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSuccess(false);
    setFormData({ name: "", email: "", date: "", timeSlot: "Afternoon", topic: "" });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-card border-border/50 shadow-2xl">
        {isSuccess ? (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-10 w-10 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Request Received!</h2>
            <p className="text-muted-foreground px-8 mb-8">
              We've received your meeting request for <span className="font-bold text-foreground">{formData.date}</span>. Our team will contact you shortly at <span className="font-bold text-foreground">{formData.email}</span> to confirm the exact time.
            </p>
            <Button onClick={resetForm} className="w-full max-w-[200px]">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-bold flex items-center gap-3">
                <CalendarIcon className="h-6 w-6 text-primary" />
                Request a Discovery Call
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Pick a date that works for you, and we'll handle the rest. No complex scheduling required.
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input 
                    id="name" 
                    placeholder="John Doe" 
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email *</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="john@company.com" 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    required 
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Preferred Date *</Label>
                  <Input 
                    id="date" 
                    type="date" 
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.date}
                    onChange={e => setFormData({...formData, date: e.target.value})}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeSlot">Time Preference</Label>
                  <Select 
                    value={formData.timeSlot} 
                    onValueChange={v => setFormData({...formData, timeSlot: v})}
                  >
                    <SelectTrigger id="timeSlot">
                      <SelectValue placeholder="Select Slot" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Morning">Morning (9AM - 12PM)</SelectItem>
                      <SelectItem value="Afternoon">Afternoon (12PM - 4PM)</SelectItem>
                      <SelectItem value="Evening">Evening (4PM - 7PM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="topic">What would you like to discuss?</Label>
                <Textarea 
                  id="topic" 
                  placeholder="Tell us a bit about your project goals..." 
                  className="h-24 resize-none"
                  value={formData.topic}
                  onChange={e => setFormData({...formData, topic: e.target.value})}
                />
              </div>

              <DialogFooter className="pt-4">
                <Button variant="ghost" type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting} className="min-w-[140px]">
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" /> Send Request
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
